// src/routes/__root.jsx
import { createRootRoute } from '@tanstack/react-router'
import { Outlet, HeadContent, Scripts } from '@tanstack/react-router'
import { HelmetProvider } from 'react-helmet-async';
import NotFoundPage from '../pages/404'
import { DestinationContext } from '../contexts/DestinationContext';
import { getDestination } from '../server/getDestination';
import { sanitizeHtml } from '../server/utlis/sanitizeHtml';
import fallbackDestination from '../contexts/fallbackDestination';

// These fields were converted from plain text to admin-editable rich text
// (Quill HTML — bold/italic/underline/lists, some with links). Render them
// through the HTML-safe path, not as plain {text}, or literal <p>/<strong>
// tags show up on the page. Sanitize once here so every consumer downstream
// (Hero, Intro, GalleryStrip, ExtraInformation, MaraGuide, GuestVoices,
// FinalCta, BestTimeCalendar) can safely dangerouslySetInnerHTML them.
const sanitizeDestinationRichText = async (destination) => {
    if (!destination) return destination;

    const [
        mainHeader,
        aboutTitle,
        galleryTitle,
        howItWorksTitle,
        maraGuideTitle,
        guestVoicesTitle,
        finalCtaTitle,
        headlineStatLabel,
    ] = await Promise.all([
        destination.mainHeader ? sanitizeHtml({ data: { html: destination.mainHeader } }) : Promise.resolve(destination.mainHeader),
        destination.about?.title ? sanitizeHtml({ data: { html: destination.about.title } }) : Promise.resolve(destination.about?.title),
        destination.galleryContent?.title ? sanitizeHtml({ data: { html: destination.galleryContent.title } }) : Promise.resolve(destination.galleryContent?.title),
        destination.howItWorks?.title ? sanitizeHtml({ data: { html: destination.howItWorks.title } }) : Promise.resolve(destination.howItWorks?.title),
        destination.maraGuide?.title ? sanitizeHtml({ data: { html: destination.maraGuide.title } }) : Promise.resolve(destination.maraGuide?.title),
        destination.guestVoices?.title ? sanitizeHtml({ data: { html: destination.guestVoices.title } }) : Promise.resolve(destination.guestVoices?.title),
        destination.finalCta?.title ? sanitizeHtml({ data: { html: destination.finalCta.title } }) : Promise.resolve(destination.finalCta?.title),
        destination.bestTimeToVisit?.headlineStatLabel ? sanitizeHtml({ data: { html: destination.bestTimeToVisit.headlineStatLabel } }) : Promise.resolve(destination.bestTimeToVisit?.headlineStatLabel),
    ]);

    return {
        ...destination,
        mainHeader,
        about: destination.about ? { ...destination.about, title: aboutTitle } : destination.about,
        galleryContent: destination.galleryContent ? { ...destination.galleryContent, title: galleryTitle } : destination.galleryContent,
        howItWorks: destination.howItWorks ? { ...destination.howItWorks, title: howItWorksTitle } : destination.howItWorks,
        maraGuide: destination.maraGuide ? { ...destination.maraGuide, title: maraGuideTitle } : destination.maraGuide,
        guestVoices: destination.guestVoices ? { ...destination.guestVoices, title: guestVoicesTitle } : destination.guestVoices,
        finalCta: destination.finalCta ? { ...destination.finalCta, title: finalCtaTitle } : destination.finalCta,
        bestTimeToVisit: destination.bestTimeToVisit ? { ...destination.bestTimeToVisit, headlineStatLabel } : destination.bestTimeToVisit,
    };
};
import RegistrationTabContextProvider from '../contexts/RegistrationTabContext';
import { AccomodationListContextProvider } from '../contexts/AccomodationListContext';
import Layout from '../components/Layout/Layout';
import Analytics from '../components/Analytics';
import RoutePending from '../components/utils/RoutePending/RoutePending';
import "bootstrap/dist/css/bootstrap.min.css";
import "react-datepicker/dist/react-datepicker.css";

import { filterActiveDestination } from '../utils/photoUtils';

export const Route = createRootRoute({
    loader: async () => {
        try {
            const destination = await getDestination();
            return { destination: await sanitizeDestinationRichText(filterActiveDestination(destination)) };
        } catch (error) {
            console.error('Failed to load destination:', error);
            return { destination: filterActiveDestination(fallbackDestination) };
        }
    },
    head: () => ({
        meta: [
            { charSet: 'utf-8' },
            { name: 'viewport', content: 'width=device-width, initial-scale=1' },
            { name: 'theme-color', content: '#000000' },
            { name: 'google-site-verification', content: 'vwGDU4_DLHWAXSARGnwiIBN6OtCQG-1sR2GcVgQ9A9Q' },
        ],
        links: [
            { rel: 'icon', href: '/wb-fav-icon.png' },
            { rel: 'manifest', href: '/manifest.json' },
            // Stylesheets & Web Fonts
            { rel: 'stylesheet', href: '/fonts/stylesheet.css' },
            { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
            { rel: 'preconnect', href: 'https://fonts.gstatic.com', crossOrigin: 'anonymous' },
            { rel: 'stylesheet', href: 'https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,500;0,600;0,700;1,500;1,600&family=Inter:wght@300;400;500;600;700&display=swap' },
        ],
        title: 'Wilderness Bookings',
    }),
    pendingComponent: RoutePending,
    pendingMs: 200,
    pendingMinMs: 150,
    notFoundComponent: NotFoundPage,
    component: RootComponent,
})

export function RootComponent() {
    const { destination } = Route.useLoaderData();

    return (
        <html lang="en">
            <head>
                <HeadContent />
            </head>
            <body
                suppressHydrationWarning
            // Only suppress hydration warnings on the body because browser extensions can inject attrs such as
            // data-new-gr-c-s-check-loaded and data-gr-ext-installed after server render.
            >
                <noscript>You need to enable JavaScript to run this app.</noscript>

                <HelmetProvider>
                    <DestinationContext.Provider value={destination}>
                        <Analytics />
                        <RegistrationTabContextProvider>
                            <AccomodationListContextProvider>
                                <Layout>
                                    <Outlet />
                                </Layout>
                            </AccomodationListContextProvider>
                        </RegistrationTabContextProvider>
                    </DestinationContext.Provider>
                </HelmetProvider>

                {/* Critical: Injects the build bundle scripts required to hydrate the client-side app */}
                <Scripts />
            </body>
        </html>
    )
}